import { ThemeEvents } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';
import {
  appendCharm,
  buildPersonalizedCartItems,
  findUnavailableSelections,
  getCatalogColors,
  getColorProduct,
  removeCharm,
  sumCharmPrice,
} from './personalize-with-charms-model.js';

class PersonalizeWithCharms extends HTMLElement {
  #catalog = { colors: [] };
  #savedSelections = [];
  #draftSelections = [];
  #activeCharmColor = '';
  #openingBaseColorId = '';
  #committing = false;
  #unavailableUids = new Set();
  #abortController = new AbortController();

  connectedCallback() {
    const catalogNode = this.querySelector('[data-charm-catalog]');
    if (!catalogNode?.textContent) return;

    try {
      this.#catalog = JSON.parse(catalogNode.textContent);
    } catch (error) {
      console.error('Unable to read the charm catalog.', error);
      return;
    }

    const colors = getCatalogColors(this.#catalog.colors || []);
    this.#activeCharmColor = colors.find((color) => this.#colorHasAvailableCharm(color)) || colors[0] || '';

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });
    this.querySelector('dialog-component')?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClosed, {
      signal,
    });

    const eventRoot = this.closest('.shopify-section') || document;
    eventRoot.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.#syncBaseColorFromPicker();
    this.#render();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  hasSavedDesign() {
    return this.#savedSelections.length > 0;
  }

  getSavedSelections() {
    return this.#savedSelections.map((selection) => ({ ...selection }));
  }

  async getPersonalizedCartItems({ baseVariantId, baseQuantity, baseProperties = {} }) {
    if (!this.hasSavedDesign()) return null;

    this.#hideError();

    try {
      const liveAvailability = await this.#fetchLiveAvailability();
      this.#applyLiveAvailability(liveAvailability);
      const unavailable = findUnavailableSelections(this.#savedSelections, liveAvailability);

      if (unavailable.length > 0) {
        this.#draftSelections = this.getSavedSelections();
        this.#unavailableUids = new Set(unavailable.map((selection) => selection.uid));
        const unavailableNames = unavailable.map((selection) => `${selection.label} (${selection.color})`).join(', ');
        this.#showError(`These charms are no longer available: ${unavailableNames}. Remove them or choose another color.`);
        this.#openDialog({ preserveError: true });
        return null;
      }

      const setId = globalThis.crypto?.randomUUID?.() || `charm-set-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      return buildPersonalizedCartItems({
        baseVariantId,
        baseQuantity,
        baseProperties,
        selections: this.#savedSelections,
        setId,
      });
    } catch (error) {
      console.error('Unable to validate charm availability.', error);
      this.#draftSelections = this.getSavedSelections();
      this.#showError('We could not confirm charm availability. Please try again.');
      this.#openDialog({ preserveError: true });
      return null;
    }
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!(target instanceof HTMLButtonElement) || !this.contains(target)) return;

    const action = target.dataset.action;
    if (action === 'open') {
      this.#openDialog();
    } else if (action === 'close') {
      this.#closeDialog(false);
    } else if (action === 'select-base-color') {
      this.#selectBaseColor(target.dataset.optionValueId || '');
    } else if (action === 'select-charm-color') {
      this.#activeCharmColor = target.dataset.color || '';
      this.#hideError();
      this.#render();
    } else if (action === 'add-charm') {
      this.#addCharm(target.dataset.character || '');
    } else if (action === 'remove-charm') {
      this.#removeCharm(target.dataset.uid || '');
    } else if (action === 'clear') {
      this.#draftSelections = [];
      this.#unavailableUids.clear();
      this.#announce('All charms removed.');
      this.#render();
    } else if (action === 'save') {
      this.#saveDesign();
    }
  };

  #openDialog({ preserveError = false } = {}) {
    this.#draftSelections = this.getSavedSelections();
    this.#committing = false;
    this.#openingBaseColorId = this.#currentBaseColorId();
    if (!preserveError) {
      this.#unavailableUids.clear();
      this.#hideError();
    }
    this.#render();
    this.querySelector('dialog-component')?.showDialog?.();
    if (preserveError) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.querySelector('[data-error]:not([hidden])')?.focus());
      });
    }
  }

  #closeDialog(commit) {
    this.#committing = commit;
    this.querySelector('dialog-component')?.closeDialog?.();
  }

  #handleDialogClosed = () => {
    if (!this.#committing && this.#openingBaseColorId && this.#currentBaseColorId() !== this.#openingBaseColorId) {
      this.#selectBaseColor(this.#openingBaseColorId);
    }

    this.#draftSelections = this.getSavedSelections();
    this.#unavailableUids.clear();
    this.#hideError();
    this.#render();
    this.querySelector('[data-action="open"]')?.focus();
  };

  #saveDesign() {
    const unavailableDraft = this.#draftSelections.filter((selection) => this.#unavailableUids.has(selection.uid));
    if (unavailableDraft.length > 0) {
      this.#showError('Remove unavailable charms before saving this set.');
      return;
    }

    this.#savedSelections = this.#draftSelections.map((selection) => ({ ...selection }));
    this.#updateTrigger();
    this.#syncAcceleratedCheckout();
    this.#announce(
      this.#savedSelections.length > 0
        ? `${this.#savedSelections.length} charms saved to this set.`
        : 'Charm personalization cleared.'
    );
    this.#closeDialog(true);
  }

  #addCharm(character) {
    const colorProduct = getColorProduct(this.#catalog.colors, this.#activeCharmColor);
    if (!colorProduct || !character) return;

    const before = this.#draftSelections.length;
    const uid = globalThis.crypto?.randomUUID?.() || `charm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.#draftSelections = appendCharm(this.#draftSelections, colorProduct, character, uid);
    if (this.#draftSelections.length === before) return;

    const added = this.#draftSelections.at(-1);
    this.#announce(`${added.label}, ${added.color}, added at position ${this.#draftSelections.length}.`);
    this.#render();
    requestAnimationFrame(() => {
      const track = this.querySelector('[data-preview-track]');
      track?.scrollTo?.({ left: track.scrollWidth, behavior: 'smooth' });
    });
  }

  #removeCharm(uid) {
    const index = this.#draftSelections.findIndex((selection) => selection.uid === uid);
    if (index < 0) return;
    const [removed] = this.#draftSelections.slice(index, index + 1);
    this.#draftSelections = removeCharm(this.#draftSelections, uid);
    this.#unavailableUids.delete(uid);
    this.#announce(`${removed.label}, ${removed.color}, removed.`);
    this.#render();
  }

  #render() {
    this.#renderBaseColors();
    this.#renderCharmColors();
    this.#renderGrid();
    this.#renderPreview();
    this.#updateTotals();
  }

  #renderBaseColors() {
    const selectedId = this.#currentBaseColorId();
    for (const button of this.querySelectorAll('[data-action="select-base-color"]')) {
      const selected = button.dataset.optionValueId === selectedId;
      button.setAttribute('aria-pressed', String(selected));
      if (selected) this.#applyStrapColor(button);
    }
  }

  #renderCharmColors() {
    const container = this.querySelector('[data-charm-colors]');
    if (!container) return;
    container.replaceChildren();

    for (const colorProduct of this.#catalog.colors) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'personalize-with-charms__color-pill';
      button.dataset.action = 'select-charm-color';
      button.dataset.color = colorProduct.name;
      button.setAttribute('aria-pressed', String(colorProduct.name === this.#activeCharmColor));
      button.disabled = !(colorProduct.characters || []).some((character) => character.available === true);
      button.textContent = colorProduct.name;
      button.style.setProperty('--personalize-charm-color', 'var(--color-background)');
      button.style.setProperty('--personalize-charm-text', 'var(--color-foreground)');
      container.append(button);
    }
  }

  #renderGrid() {
    const grid = this.querySelector('[data-charm-grid]');
    if (!grid) return;
    grid.replaceChildren();

    const colorProduct = getColorProduct(this.#catalog.colors, this.#activeCharmColor);
    for (const character of colorProduct?.characters || []) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'personalize-with-charms__charm-card';
      button.dataset.action = 'add-charm';
      button.dataset.character = character.label;
      button.disabled = !character.available;
      button.setAttribute('aria-label', `${character.label}, ${colorProduct.name}${character.available ? '' : ', unavailable'}`);

      const media = document.createElement('span');
      media.className = 'personalize-with-charms__charm-card-media';
      if (character.image) {
        const image = document.createElement('img');
        image.src = character.image;
        image.alt = '';
        image.loading = 'lazy';
        media.append(image);
      } else {
        media.textContent = character.label;
      }

      const label = document.createElement('span');
      label.className = 'personalize-with-charms__charm-card-label';
      label.textContent = character.label;
      button.append(media, label);
      grid.append(button);
    }
  }

  #renderPreview() {
    const track = this.querySelector('[data-preview-track]');
    if (!track) return;
    track.replaceChildren();

    this.#draftSelections.forEach((selection, index) => {
      const item = document.createElement('span');
      item.className = 'personalize-with-charms__preview-item';
      item.dataset.uid = selection.uid;
      if (this.#unavailableUids.has(selection.uid)) item.dataset.unavailable = 'true';

      if (selection.image) {
        const image = document.createElement('img');
        image.src = selection.image;
        image.alt = `${selection.label}, ${selection.color}`;
        item.append(image);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'personalize-with-charms__preview-fallback';
        fallback.textContent = selection.label;
        item.append(fallback);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'personalize-with-charms__remove';
      remove.dataset.action = 'remove-charm';
      remove.dataset.uid = selection.uid;
      remove.setAttribute('aria-label', `Remove ${selection.label}, ${selection.color}, position ${index + 1}`);
      remove.textContent = '×';
      item.append(remove);
      track.append(item);
    });

    const count = this.#draftSelections.length;
    const countText = `${count} ${count === 1 ? 'charm' : 'charms'}`;
    const previewCount = this.querySelector('[data-preview-count]');
    if (previewCount) previewCount.textContent = countText;
    const clear = this.querySelector('[data-action="clear"]');
    if (clear instanceof HTMLButtonElement) clear.disabled = count === 0;
  }

  #updateTotals() {
    const count = this.#draftSelections.length;
    const saveCount = this.querySelector('[data-save-count]');
    const saveTotal = this.querySelector('[data-save-total]');
    if (saveCount) saveCount.textContent = `${count} ${count === 1 ? 'CHARM' : 'CHARMS'}`;
    if (saveTotal) {
      saveTotal.textContent = formatMoney(
        sumCharmPrice(this.#draftSelections),
        this.dataset.moneyFormat || '${{amount}}',
        this.dataset.currency || 'USD'
      );
    }
  }

  #updateTrigger() {
    const label = this.querySelector('[data-trigger-label]');
    if (!label) return;
    label.textContent = this.hasSavedDesign()
      ? `${this.dataset.editLabel || 'EDIT CHARMS'} · ${this.#savedSelections.length} SELECTED`
      : this.dataset.buttonLabel || 'PERSONALIZE WITH CHARMS';
  }

  #syncAcceleratedCheckout() {
    const container = this.#productDetails()?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (!(container instanceof HTMLElement)) return;

    if (this.hasSavedDesign()) {
      container.hidden = true;
      container.dataset.hiddenByCharms = 'true';
      return;
    }

    if (container.dataset.hiddenByCharms === 'true') {
      delete container.dataset.hiddenByCharms;
      const addButton = this.#productDetails()?.querySelector('[ref="addToCartButton"]');
      container.hidden = addButton instanceof HTMLButtonElement ? addButton.disabled : false;
    }
  }

  #selectBaseColor(optionValueId) {
    if (!optionValueId) return;
    const picker = this.#variantPicker();
    if (!picker) return;
    const position = this.dataset.baseOptionPosition || '';
    const controls = picker.querySelectorAll(`[data-input-id^="${position}-"]`);
    const control = Array.from(controls).find((item) => item.dataset.optionValueId === optionValueId);

    if (control instanceof HTMLInputElement) {
      if (control.disabled || control.getAttribute('aria-disabled') === 'true') return;
      control.checked = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (control instanceof HTMLOptionElement && control.parentElement instanceof HTMLSelectElement) {
      control.parentElement.value = control.value;
      control.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const modalButton = Array.from(this.querySelectorAll('[data-action="select-base-color"]')).find(
      (button) => button.dataset.optionValueId === optionValueId
    );
    if (modalButton) this.#applyStrapColor(modalButton);
    this.#renderBaseColors();
  }

  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== String(this.dataset.productId)) return;
    this.#syncBaseColorFromPicker();
    this.#syncAcceleratedCheckout();
  };

  #syncBaseColorFromPicker() {
    const selectedId = this.#selectedPickerOptionId();
    if (!selectedId) return;
    for (const button of this.querySelectorAll('[data-action="select-base-color"]')) {
      const selected = button.dataset.optionValueId === selectedId;
      button.setAttribute('aria-pressed', String(selected));
      if (selected) this.#applyStrapColor(button);
    }
  }

  #selectedPickerOptionId() {
    const picker = this.#variantPicker();
    if (!picker) return '';
    const position = this.dataset.baseOptionPosition || '';
    const controls = picker.querySelectorAll(`[data-input-id^="${position}-"]`);
    for (const control of controls) {
      if (control instanceof HTMLInputElement && control.checked) return control.dataset.optionValueId || '';
      if (control instanceof HTMLOptionElement && control.selected) return control.dataset.optionValueId || '';
    }
    return '';
  }

  #currentBaseColorId() {
    return this.#selectedPickerOptionId() || this.querySelector('[data-action="select-base-color"][aria-pressed="true"]')?.dataset.optionValueId || '';
  }

  #applyStrapColor(button) {
    const strap = this.querySelector('[data-preview-strap]');
    if (!(strap instanceof HTMLElement)) return;
    strap.style.setProperty('--personalize-strap-color', button.dataset.previewColor || '#e6e2da');
    if (button.dataset.previewImage) {
      strap.style.setProperty('--personalize-strap-image', `url("${button.dataset.previewImage}")`);
    } else {
      strap.style.removeProperty('--personalize-strap-image');
    }
  }

  #variantPicker() {
    const productId = String(this.dataset.productId);
    return Array.from(this.#productDetails()?.querySelectorAll('variant-picker') || []).find(
      (picker) => String(picker.dataset.productId) === productId
    );
  }

  #productDetails() {
    return this.closest('.product-details');
  }

  #colorHasAvailableCharm(color) {
    const colorProduct = getColorProduct(this.#catalog.colors, color);
    return colorProduct?.characters?.some((character) => character.available === true) === true;
  }

  async #fetchLiveAvailability() {
    const handles = [...new Set(this.#savedSelections.map((selection) => selection.handle))];
    const root = globalThis.Shopify?.routes?.root || '/';
    const products = await Promise.all(
      handles.map(async (handle) => {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Unable to load charm product ${handle}`);
        return response.json();
      })
    );

    const availability = new Map();
    for (const product of products) {
      for (const variant of product.variants || []) {
        availability.set(String(variant.id), variant.available === true);
      }
    }
    return availability;
  }

  #applyLiveAvailability(liveAvailability) {
    for (const colorProduct of this.#catalog.colors) {
      for (const character of colorProduct.characters || []) {
        const variantId = String(character.id);
        if (liveAvailability.has(variantId)) {
          character.available = liveAvailability.get(variantId) === true;
        }
      }
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = false;
    requestAnimationFrame(() => error.focus());
  }

  #hideError() {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;
    error.hidden = true;
    error.textContent = '';
  }

  #announce(message) {
    const liveRegion = this.querySelector('[data-live-region]');
    if (liveRegion) liveRegion.textContent = message;
  }
}

if (!customElements.get('personalize-with-charms')) {
  customElements.define('personalize-with-charms', PersonalizeWithCharms);
}

